import { ANY_FUNCTION, compileModifier, createForm, foldDigits, functionTable, kindProblems, parseExpression, type Field, type FunctionsKnown, type FunctionTable, type KindProblem, type Page } from '@fieldia/core';
import { uncalled } from './uncalled';
import type { DesignerWords } from './designer-words';
import { en } from './locales/en';

/**
 * A formula as a person types it — "Worked out from" a field's value, or a
 * value to set when something holds: what is wrong with it and where (by
 * character, counted from 1, as an editor counts them), what it reads, how it
 * reads in words, and what it gives on made-up values. The page's own parser
 * decides what reads; this only says where it stopped, and in words.
 */

/** What is wrong, and the characters it is about, from 1; none when it is about the whole. */
export interface FormulaProblem {
  words: string;
  from?: number;
  to?: number;
  /** Said as it is, never as a formula's that the page keeps none of: what is typed is no formula (an address as words). */
  plain?: true;
}

/** A piece of a formula, and where it is: `from` and `to` count characters from 0, `to` not included. */
export interface FormulaToken {
  kind: 'name' | 'number' | 'text' | 'list' | 'sign' | 'word' | 'open' | 'close' | 'comma';
  text: string;
  from: number;
  to: number;
}

/** The same pieces, in the same order, as the page's own reader takes them: a number in Arabic or Persian digits too, with ٫. */
const PIECE = /(\(|\)|,|==|!=|<=|>=|<>|<|>|[-+*/%]|not\s+in\b|in\b|and\b|or\b|not\b|\[[^\]]*\]|'[^']*'|"[^"]*"|[\d\u0660-\u0669\u06f0-\u06f9]+[.\u066b]?[\d\u0660-\u0669\u06f0-\u06f9]*|\w+(?:\.\w+)*)/iy;
/** A number piece's value, whatever its digits: "٢٫٥" is 2.5. */
const numberOf = (text: string) => Number(foldDigits(text).replace('\u066b', '.'));
const SPACE = /\s*/y;
const WORDS = new Set(['and', 'or', 'not', 'in']);
const CONSTANTS = new Set(['True', 'False', 'None', 'true', 'false', 'null']);

function kindOf(text: string): FormulaToken['kind'] {
  if (text === '(') return 'open';
  if (text === ')') return 'close';
  if (text === ',') return 'comma';
  if (text.startsWith('[')) return 'list';
  if (text.startsWith("'") || text.startsWith('"')) return 'text';
  if (/^[\d\u0660-\u0669\u06f0-\u06f9]/.test(text)) return 'number';
  if (WORDS.has(text.toLowerCase().split(/\s+/)[0]) || CONSTANTS.has(text)) return 'word';
  if (/^\w/.test(text)) return 'name';
  return 'sign';
}

/** The pieces of a formula, or the character it cannot read. */
export function formulaTokens(source: string): FormulaToken[] | { bad: number } {
  const tokens: FormulaToken[] = [];
  let at = 0;
  for (;;) {
    SPACE.lastIndex = at;
    SPACE.exec(source);
    at = SPACE.lastIndex;
    if (at >= source.length) return tokens;
    PIECE.lastIndex = at;
    const match = PIECE.exec(source);
    if (!match) return { bad: at };
    tokens.push({ kind: kindOf(match[0]), text: match[0], from: at, to: PIECE.lastIndex });
    at = PIECE.lastIndex;
  }
}

/** "Unknown field “prise” at 1–5": the words, and where, as they are shown under the box. */
export function problemWords(problem: FormulaProblem | null, words: DesignerWords = en): string {
  if (!problem) return '';
  if (problem.from === undefined) return problem.words;
  const at = problem.to === undefined || problem.to === problem.from ? `${problem.from}` : `${problem.from}–${problem.to}`;
  // Words that go on past the place end in a comma of their own: "“round” takes 1 or 2 values, at 9–13".
  return words.rules.at(problem.words, at);
}

const place = (token: FormulaToken) => ({ from: token.from + 1, to: token.to });

/** A field called as a function is a function: "round(" is not a field "round". */
const calls = (tokens: FormulaToken[], i: number) => tokens[i + 1]?.kind === 'open';

/** The app's functions as a formula calls them; none when their names cannot be (the designer was given a name no page may call). */
function tableOf(functions: FunctionsKnown | undefined): FunctionTable | undefined {
  try {
    return functionTable(functions);
  } catch {
    return undefined;
  }
}

/** Whether it reads at all: the page's own reader, with the app's functions, stopped where it stops. */
function readError(source: string, functions?: FunctionsKnown): string | null {
  try {
    compileModifier(source, tableOf(functions));
    return null;
  } catch (error) {
    return (error as Error).message;
  }
}

/** A message that only says the formula ran out: what came before it may still go on to read. */
const ranOut = (message: string) => /EOF|^Expected/.test(message);

/**
 * What is wrong with a formula on this page, or null when it reads: a sign it
 * cannot hold, a piece out of place, something missing, a function there is
 * not — Fieldia's or the app's (`functions`) — or given too many values, a
 * field the page does not have. A value of a kind a function does not take
 * stops nothing: `formulaAdvice` says it.
 */
export function formulaProblem(page: Page, source: string, words: DesignerWords = en, functions?: FunctionsKnown): FormulaProblem | null {
  const w = words.rules;
  if (!source.trim()) return { words: w.typeAFormula };
  const tokens = formulaTokens(source);
  if (!Array.isArray(tokens)) return { words: w.cannotBeIn(source[tokens.bad]), from: tokens.bad + 1, to: tokens.bad + 1 };
  const message = readError(source, functions);
  if (message !== null) return whereItStops(source, tokens, message, words, functions);
  // It reads: each name must be a field of the page.
  for (const [i, token] of tokens.entries()) {
    if (token.kind !== 'name' || calls(tokens, i)) continue;
    const [root, part] = token.text.split('.');
    if (Object.prototype.hasOwnProperty.call(page.fields, root)) continue;
    // What every formula on a record reads besides its fields: its id, the values the app passes in, and the person.
    if (root === 'id' || root === 'context') continue;
    if (root === 'user') {
      if (part !== undefined && !['id', 'name', 'roles'].includes(part)) return { words: w.personHas(part), ...place(token) };
      continue;
    }
    return { words: w.unknownField(root), ...place(token) };
  }
  return null;
}

/**
 * What a formula that reads hands a function of a kind it does not take, in words and where — "“age” takes a date:
 * Notes is text" — or null. Said, never refused: such a call gives nothing, as it did before kinds were read.
 */
export function formulaAdvice(page: Page, source: string, words: DesignerWords = en, functions?: FunctionsKnown): FormulaProblem | null {
  const tokens = formulaTokens(source);
  if (!Array.isArray(tokens) || readError(source, functions) !== null) return null;
  const kind = kindProblems(parseExpression(source, tableOf(functions)), { fields: page.fields }, functions)[0];
  return kind ? kindSaid(page, tokens, kind, words) : null;
}

/** A value of a kind a function does not take, in words — a field by its label — and where it is. */
function kindSaid(page: Page, tokens: FormulaToken[], problem: KindProblem, words: DesignerWords): FormulaProblem {
  const w = words.rules;
  const given = problem.given;
  const at =
    given.type === 'Identifier' || given.type === 'Call'
      ? tokens.find((t, i) => t.kind === 'name' && t.text === given.name && calls(tokens, i) === (given.type === 'Call'))
      : given.type === 'Literal'
        ? tokens.find((t) => (t.kind === 'text' || t.kind === 'number' || t.kind === 'word' || t.kind === 'list') && literalOf(t.text)?.value === given.value)
        : undefined;
  const said = given.type === 'Identifier' && page.fields[given.name] ? labelOf(page, given.name) : given.type === 'Call' ? `${given.name}(…)` : (at?.text ?? '');
  const text = w.wrongKind(problem.name, problem.wants, said, problem.got);
  return at ? { words: text, ...place(at) } : { words: text };
}

function whereItStops(source: string, tokens: FormulaToken[], message: string, words: DesignerWords, functions?: FunctionsKnown): FormulaProblem {
  const w = words.rules;
  const named = /^no function "(\w+)"/.exec(message) ?? /^(\w+) takes /.exec(message) ?? /^(sum) takes the name/.exec(message);
  if (named) {
    // The call the reader stopped at: the last one of that name before where it stopped.
    const at = firstFailing(source, tokens, functions);
    const name = [...tokens.slice(0, at + 1)].reverse().find((t) => t.kind === 'name' && t.text === named[1]) ?? tokens.find((t) => t.text === named[1]);
    const said = /^no function/.test(message) ? w.unknownFunction(named[1]) : w.takes(named[1], message.slice(named[1].length));
    return name ? { words: said, ...place(name) } : { words: said };
  }
  if (ranOut(message)) {
    const open = unclosed(tokens);
    if (open) return { words: w.neverClosed, ...place(open) };
    const last = tokens[tokens.length - 1];
    return { words: w.missingAfter(last.text), ...place(last) };
  }
  const token = tokens[firstFailing(source, tokens, functions)];
  return { words: w.outOfPlace(token.text), ...place(token) };
}

/** The first piece the formula cannot go on from: up to it, the formula reads, or only runs out. */
function firstFailing(source: string, tokens: FormulaToken[], functions?: FunctionsKnown): number {
  for (let i = 0; i < tokens.length; i++) {
    const message = readError(source.slice(0, tokens[i].to), functions);
    if (message !== null && !ranOut(message)) return i;
  }
  return tokens.length - 1;
}

/** The first bracket opened and never closed. */
function unclosed(tokens: FormulaToken[]): FormulaToken | undefined {
  const open: FormulaToken[] = [];
  for (const token of tokens) {
    if (token.kind === 'open') open.push(token);
    else if (token.kind === 'close') open.pop();
  }
  return open[0];
}

/** The fields a formula reads, by name, once each, whatever functions it calls; none when it cannot be read. */
export function fieldsReadBy(source: string | boolean | undefined): string[] {
  if (typeof source !== 'string') return [];
  try {
    return [...compileModifier(source, ANY_FUNCTION).fields];
  } catch {
    return [];
  }
}

const labelOf = (page: Page, name: string) => page.fields[name]?.label || name;

/**
 * Whether working a field out from this formula would have it worked out
 * from itself, directly or through other worked-out fields; the words if so.
 */
export function wouldCircle(page: Page, own: string, source: string, words: DesignerWords = en): string | null {
  const reads = (name: string) => (name === own ? fieldsReadBy(source) : fieldsReadBy(page.fields[name]?.compute));
  if (reads(own).includes(own)) return words.rules.fromItself(labelOf(page, own));
  const path: string[] = [own];
  const seen = new Set<string>();
  const visit = (name: string): boolean => {
    for (const next of reads(name)) {
      if (next === own) return true;
      if (seen.has(next)) continue;
      seen.add(next);
      path.push(next);
      if (visit(next)) return true;
      path.pop();
    }
    return false;
  };
  if (!visit(own)) return null;
  return words.rules.circle(labelOf(page, own), [...path, own].map((name) => labelOf(page, name)));
}

// ---- in words ------------------------------------------------------------------------------------

const SIGNS: Record<string, string> = { '*': '×', '/': '÷', '-': '−', '>=': '≥', '<=': '≤' };
/** Signs said in words: `==` as "is". */
const SAID_SIGNS: Record<string, 'is' | 'isNot'> = { '==': 'is', '!=': 'isNot', '<>': 'isNot' };
const signWords = (sign: string, words: DesignerWords) => (SAID_SIGNS[sign] ? words.rules.signs[SAID_SIGNS[sign]] : (SIGNS[sign] ?? sign));

/** A label that holds a sign of its own, "Price - net", is quoted, so it reads as one name and not as a sum. */
const nameWords = (label: string, words: DesignerWords) => (/[-+*/%<>=!(),'"×÷−≥≤]/.test(label) ? words.rules.quoteName(label) : label);

/** A literal as written, as its value: `'bulk'` is bulk, `-5` is −5; null for anything else. */
export function literalOf(source: string): { value: string | number | boolean | null } | null {
  const tokens = formulaTokens(source.trim());
  if (!Array.isArray(tokens)) return null;
  const [first, second] = tokens;
  if (tokens.length === 2 && first.text === '-' && second.kind === 'number') return { value: -numberOf(second.text) };
  if (tokens.length !== 1) return null;
  if (first.kind === 'text') return { value: first.text.slice(1, -1) };
  if (first.kind === 'number') return { value: numberOf(first.text) };
  if (first.kind === 'word' && CONSTANTS.has(first.text)) return { value: /^t/i.test(first.text) ? true : /^f/i.test(first.text) ? false : null };
  return null;
}

/** A value as a person reads it: a choice by its label, yes or no, text as it is. */
export function valueInWords(field: Field | undefined, value: unknown, words: DesignerWords = en): string {
  if (field?.type === 'selection') return field.options.find((o) => o.value === value)?.label ?? String(value);
  if (typeof value === 'boolean') return value ? words.rules.yes : words.rules.no;
  if (value === null || value === undefined) return words.rules.nothing;
  return String(value);
}

/** A piece of a formula in words, with the space before it and what it was written as; `field` names the field it stands for. */
export interface WordPiece {
  text: string;
  was: string;
  space: boolean;
  field?: string;
}

/** A formula in words, piece by piece: what `formulaInWords` joins, and the box draws with each field marked. */
export function formulaPieces(page: Page, source: string, words: DesignerWords = en): WordPiece[] {
  const tokens = formulaTokens(source);
  if (!Array.isArray(tokens)) return [{ text: source, was: source, space: false }];
  return tokens.map((token, i) => {
    let text = token.text;
    let field: string | undefined;
    if (token.kind === 'name' && !calls(tokens, i)) {
      field = token.text.split('.')[0];
      text = nameWords(labelOf(page, field), words);
    } else if (token.kind === 'sign') text = signWords(token.text, words);
    else if (token.kind === 'word' && CONSTANTS.has(token.text)) text = valueInWords(undefined, literalOf(token.text)?.value, words);
    else if (token.kind === 'word') text = words.rules.keyword(token.text);
    else if (token.kind === 'text') {
      // Compared with a choice: the choice's label.
      const compared = tokens[i - 1]?.kind === 'sign' && tokens[i - 2]?.kind === 'name' ? page.fields[tokens[i - 2].text] : undefined;
      text = compared?.type === 'selection' ? valueInWords(compared, token.text.slice(1, -1), words) : words.rules.text(token.text.slice(1, -1));
    }
    const tight = token.kind === 'close' || token.kind === 'comma' || i === 0 || tokens[i - 1].kind === 'open' || (token.kind === 'open' && tokens[i - 1]?.kind === 'name');
    return { text, was: token.text, space: !tight, ...(field ? { field } : {}) };
  });
}

/**
 * A formula in words: its fields by their labels, × and ÷ for * and /, ≥
 * and ≤ for >= and <=, "is" and "is not" for == and !=, and a choice
 * compared with a value by the value's label: `state == 'done'` reads
 * "Status is Done". A label with a sign in it is quoted.
 */
export function formulaInWords(page: Page, source: string, words: DesignerWords = en): string {
  return formulaPieces(page, source, words)
    .map((piece) => (piece.space ? ' ' : '') + piece.text)
    .join('');
}

// ---- on made-up values ---------------------------------------------------------------------------

const AMOUNTS = [120, 80, 45, 15.5];
const COUNTS = [3, 5, 2, 4];
/** Days apart, so a rule comparing two dates can be seen to hold or not. */
const DAYS = ['2026-03-14', '2026-03-01', '2026-04-02'];

/** A made-up value for a field, the n-th of its kind in the formula. */
function sampleOf(name: string, field: Field, n: number): unknown {
  switch (field.type) {
    case 'integer':
      return COUNTS[n % COUNTS.length];
    case 'float':
    case 'monetary':
      return AMOUNTS[n % AMOUNTS.length];
    case 'boolean':
      return true;
    case 'selection':
      return field.multiple ? [field.options[0]?.value] : field.options[0]?.value;
    case 'date':
      return DAYS[n % DAYS.length];
    case 'datetime':
      return `${DAYS[n % DAYS.length]} 09:30:00`;
    case 'char':
    case 'text':
    case 'html':
      return field.label || name;
    default:
      return null;
  }
}

const shown = (field: Field | undefined, value: unknown, words: DesignerWords) =>
  typeof value === 'string' && field?.type !== 'selection' && !/^\d{4}-/.test(value) ? words.rules.text(value) : valueInWords(field, value, words);

/** Made-up values for the fields a formula reads, the n-th of a kind taking the n-th sample; a worked-out field is left to be worked out. */
function samplesFor(page: Page, reads: string[]): Record<string, unknown> {
  const counts = new Map<string, number>();
  const values: Record<string, unknown> = {};
  for (const name of reads) {
    const field = page.fields[name];
    const group = ['integer'].includes(field.type) ? 'count' : ['float', 'monetary'].includes(field.type) ? 'amount' : field.type;
    const n = counts.get(group) ?? 0;
    counts.set(group, n + 1);
    if (field.compute === undefined) values[name] = sampleOf(name, field, n);
  }
  return values;
}

/**
 * The values as a form settles them on these fields, worked-out ones worked out; null when they cannot be. The app's
 * functions are known, never called: on made-up values one gives nothing.
 */
function settled(fields: Page['fields'], values: Record<string, unknown>, functions?: FunctionsKnown): Record<string, unknown> | null {
  const trial: Page = { fieldia: '0.1', id: 'sample', data: { kind: 'responses' }, fields, layout: { type: 'sections', id: 'root', children: [] } };
  const known = uncalled(functions);
  try {
    return createForm({ page: trial, values: values as never, ...(known ? { functions: known } : {}) }).getState().values;
  } catch {
    return null;
  }
}

/** "Price 120", "Quantity 3": each field read, by its label, with its value. */
const givenWords = (page: Page, reads: string[], values: Record<string, unknown>, words: DesignerWords) => reads.map((name) => words.rules.given(labelOf(page, name), shown(page.fields[name], values[name], words)));

/**
 * The formula worked out on made-up values, the way the form works it out
 * (a whole number rounded, text as text): "With Price 120 and Quantity 3:
 * 360". Empty when the formula does not read.
 */
export function sampleResult(page: Page, own: string, source: string, words: DesignerWords = en, functions?: FunctionsKnown): string {
  if (formulaProblem(page, source, en, functions) || wouldCircle(page, own, source)) return '';
  const reads = fieldsReadBy(source).filter((name) => name !== own && page.fields[name]);
  const target = page.fields[own];
  if (!target) return '';
  const worked = settled({ ...page.fields, [own]: { ...target, compute: source } as Field }, samplesFor(page, reads), functions);
  if (!worked) return '';
  const result = worked[own];
  const said = result === null || result === undefined || (typeof result === 'number' && !Number.isFinite(result)) ? words.rules.cannotWorkOut : shown(target, result, words);
  if (!reads.length) return words.rules.always(said);
  return words.rules.withValues(givenWords(page, reads, worked, words), said);
}

/**
 * Whether a rule across fields holds on made-up values, as the form checks
 * it: "With Contract ends 2026-03-14 and Start date 2026-03-01: holds".
 * Empty when the formula does not read.
 */
export function sampleHolds(page: Page, source: string, words: DesignerWords = en, functions?: FunctionsKnown): string {
  const w = words.rules;
  if (formulaProblem(page, source, en, functions)) return '';
  const reads = fieldsReadBy(source).filter((name) => page.fields[name]);
  const worked = settled(page.fields, samplesFor(page, reads), functions);
  if (!worked) return '';
  const holds = compileModifier(source, ANY_FUNCTION).evaluate(worked);
  if (!reads.length) return holds ? w.alwaysHolds : w.neverHolds;
  // The form waits for every field a rule reads: one with no made-up value is said so.
  const waits = reads.filter((name) => worked[name] === null || worked[name] === undefined);
  if (waits.length) return w.checkedOnce(waits.map((name) => labelOf(page, name)));
  return w.withValues(givenWords(page, reads, worked, words), holds ? w.holds : w.doesNotHold);
}
